import { buildFusionShellUrl } from "./externalNavigation";
import { resolveEffectiveDomain } from "./domainPersistence";

export type FreyrRecipientBucket = "taskUsers" | "taskGroups" | "taskRoles";

export type FreyrRecipients = {
  taskUsers: string[];
  taskGroups: string[];
  taskRoles: string[];
};

export type FreyrRecipientOption = {
  id: string;
  label: string;
  bucket: FreyrRecipientBucket;
  /** Optional email shown as the option sub-label (same as triage notify). */
  email?: string;
};

export type ManualIaPdfShareTarget = {
  regulationId: string;
  regulationVersion: string;
  attachmentS3Uri: string;
  /** Plan & Track process URL from list `references[].name === previousForms`. */
  assessmentLink: string;
  /** Navigation `source` on `regulationDetailUrl` (e.g. assessmentCenter). */
  source?: string;
};

export type ManualAssessmentReportShareRequest = {
  regulationId: string;
  regulationVersion: string;
  freyrRecipients: FreyrRecipients;
  externalRecipients: string[];
  message: string;
  regulationDetailUrl: string;
  assessmentLink: string;
  attachmentS3Uri: string;
  sendEmailNotification: boolean;
};

/** Workflow / history `references[].name` for the Manual IA PDF. */
export const MANUAL_IMPACT_ASSESSMENT_REPORT_NAME = "Impact Assessment Report";
/** Workflow / history `references[].name` for the AI IA PDF. */
export const AI_IMPACT_ASSESSMENT_REPORT_NAME = "AI Impact Assessment Report";

/** Top-level Manual locators only — not `report_url` (that is the AI report). */
const MANUAL_ATTACHMENT_S3_URI_KEYS = [
  "attachmentS3Uri",
  "attachment_s3_uri",
  "reportS3Uri",
  "report_s3_uri",
] as const;

const AI_REPORT_URL_KEYS = ["report_url", "reportUrl"] as const;

/** local@domain.tld — rejects `test@`, `testgmail.com`, `@domain.com`. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const INVALID_SHARE_EMAIL_FORMAT_PREFIX = "Invalid email format:";
export const SHARE_SUCCESS_TOAST_MS = 5000;

const FREYR_BUCKETS: readonly FreyrRecipientBucket[] = [
  "taskUsers",
  "taskGroups",
  "taskRoles",
];

export function emptyFreyrRecipients(): FreyrRecipients {
  return { taskUsers: [], taskGroups: [], taskRoles: [] };
}

export function stripRegulationVersionPrefix(version: string): string {
  const trimmed = version.trim();
  if (!trimmed) return "";
  return /^v/i.test(trimmed) ? trimmed.slice(1).trim() : trimmed;
}

export function fileNameFromAttachmentS3Uri(uri: string): string {
  const trimmed = uri.trim();
  if (!trimmed) return "Manual Impact Assessment Report.pdf";
  const withoutQuery = trimmed.split("?")[0] ?? trimmed;
  const last = withoutQuery.split("/").pop()?.trim();
  return last || "Manual Impact Assessment Report.pdf";
}

const PREVIOUS_FORMS_REFERENCE_NAME = "previousForms";

function referenceUri(ref: unknown): string {
  if (!ref || typeof ref !== "object") return "";
  const uri = (ref as { uri?: unknown }).uri;
  return typeof uri === "string" ? uri.trim() : "";
}

function referenceName(ref: unknown): string {
  if (!ref || typeof ref !== "object") return "";
  const name = (ref as { name?: unknown }).name;
  return typeof name === "string" ? name.trim() : "";
}

function itemReferences(item: Record<string, unknown>): unknown[] {
  return Array.isArray(item.references) ? item.references : [];
}

function namesMatch(actual: string, expected: string): boolean {
  return actual.toLowerCase() === expected.toLowerCase();
}

function findReferenceUriByName(
  refs: readonly unknown[],
  expectedName: string,
): string {
  for (const ref of refs) {
    if (!namesMatch(referenceName(ref), expectedName)) continue;
    const uri = referenceUri(ref);
    if (uri) return uri;
  }
  return "";
}

function firstTopLevelString(
  item: Record<string, unknown>,
  keys: readonly string[],
): string {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

/**
 * Manual IA list / history: `references[].name === "Impact Assessment Report"`.
 * Processes started from AI IA may also carry `AI Impact Assessment Report` —
 * do not use that URI, first-PDF-wins, or top-level `report_url` (AI).
 */
export function resolveManualWorkflowAttachmentS3Uri(
  item: Record<string, unknown> | null | undefined,
): string {
  if (!item) return "";
  const byName = findReferenceUriByName(
    itemReferences(item),
    MANUAL_IMPACT_ASSESSMENT_REPORT_NAME,
  );
  if (byName) return byName;
  return firstTopLevelString(item, MANUAL_ATTACHMENT_S3_URI_KEYS);
}

/**
 * AI IA list / history: `references[].name === "AI Impact Assessment Report"`,
 * then `report_url` / `reportUrl`. Never the Manual `Impact Assessment Report`.
 */
export function resolveAiImpactAssessmentReportUri(
  item: Record<string, unknown> | null | undefined,
): string {
  if (!item) return "";
  const byName = findReferenceUriByName(
    itemReferences(item),
    AI_IMPACT_ASSESSMENT_REPORT_NAME,
  );
  if (byName) return byName;
  return firstTopLevelString(item, AI_REPORT_URL_KEYS);
}

/** List `references[]` item `name === previousForms` → Plan & Track process URI. */
export function resolveManualWorkflowPreviousFormsUri(
  item: Record<string, unknown> | null | undefined,
): string {
  if (!item) return "";
  const refs = item.references;
  if (!Array.isArray(refs)) return "";
  const found = refs.find((ref) => {
    if (!ref || typeof ref !== "object") return false;
    const name = String((ref as { name?: unknown }).name ?? "").trim();
    return name === PREVIOUS_FORMS_REFERENCE_NAME && Boolean(referenceUri(ref));
  });
  return found ? referenceUri(found) : "";
}

export function buildManualIaReportShareRegulationDetailUrl(
  regulationId: string,
  regulationVersion: string,
  options?: { source?: string; domain?: string },
): string {
  const params = new URLSearchParams();
  const version = regulationVersion.trim();
  if (version) {
    params.set("version", /^v/i.test(version) ? version : `v${version}`);
  }
  const source = options?.source?.trim();
  if (source) params.set("source", source);
  const domain =
    options?.domain?.trim() ||
    resolveEffectiveDomain({ explicit: options?.domain }) ||
    "MPR";
  if (domain) params.set("domain", domain);
  const qs = params.toString();
  const id = regulationId.trim();
  const modulePath = `/regulation-details/${id}${qs ? `?${qs}` : ""}`;
  return buildFusionShellUrl(modulePath);
}

export function isValidShareEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** Split External Recipients input on commas (empty tokens dropped). */
export function splitShareEmailInput(raw: string): string[] {
  return raw
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

export function partitionShareEmails(raw: string): {
  valid: string[];
  invalid: string[];
} {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const token of splitShareEmailInput(raw)) {
    if (isValidShareEmail(token)) valid.push(token);
    else invalid.push(token);
  }
  return { valid, invalid };
}

export function invalidShareEmailsError(
  invalid: readonly string[],
): string | null {
  if (!invalid.length) return null;
  return `${INVALID_SHARE_EMAIL_FORMAT_PREFIX} ${invalid.join(", ")}`;
}

export function normalizeExternalShareEmails(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const raw of values) {
    const email = raw.trim();
    if (!isValidShareEmail(email)) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push(email);
  }
  return emails;
}

export function countFreyrRecipients(recipients: FreyrRecipients): number {
  return (
    recipients.taskUsers.length +
    recipients.taskGroups.length +
    recipients.taskRoles.length
  );
}

export function hasShareRecipients(
  freyrRecipients: FreyrRecipients,
  externalRecipients: readonly string[],
): boolean {
  return (
    countFreyrRecipients(freyrRecipients) > 0 ||
    normalizeExternalShareEmails(externalRecipients).length > 0
  );
}

export function bucketFreyrRecipientOptions(
  selected: readonly FreyrRecipientOption[],
): FreyrRecipients {
  const next = emptyFreyrRecipients();
  for (const option of selected) {
    if (!option.id) continue;
    next[option.bucket].push(option.id);
  }
  return next;
}

function asPlainText(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  return "";
}

export function parseTaskOwnerSelectionOptions(
  body: unknown,
): FreyrRecipientOption[] {
  const payload =
    body && typeof body === "object"
      ? ((body as { data?: unknown }).data ?? body)
      : null;
  if (!payload || typeof payload !== "object") return [];

  const grouped = payload as Record<string, unknown>;
  const options: FreyrRecipientOption[] = [];
  for (const bucket of FREYR_BUCKETS) {
    const list = grouped[bucket];
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const record = item as Record<string, unknown>;
      const id =
        asPlainText(record.id) ||
        asPlainText(record.value) ||
        asPlainText(record.code);
      if (!id) continue;
      const label =
        asPlainText(record.name) ||
        asPlainText(record.label) ||
        asPlainText(record.email) ||
        id;
      const email = asPlainText(record.email);
      options.push(email ? { id, label, bucket, email } : { id, label, bucket });
    }
  }
  return options;
}

export function buildManualAssessmentReportShareRequest(
  target: ManualIaPdfShareTarget,
  freyrRecipients: FreyrRecipients,
  extras?: {
    externalRecipients?: readonly string[];
    message?: string;
    sendEmailNotification?: boolean;
  },
): ManualAssessmentReportShareRequest {
  const regulationDetailUrl = buildManualIaReportShareRegulationDetailUrl(
    target.regulationId,
    target.regulationVersion,
    { source: target.source },
  );
  return {
    regulationId: target.regulationId.trim(),
    regulationVersion: stripRegulationVersionPrefix(target.regulationVersion),
    freyrRecipients: {
      taskUsers: [...freyrRecipients.taskUsers],
      taskGroups: [...freyrRecipients.taskGroups],
      taskRoles: [...freyrRecipients.taskRoles],
    },
    externalRecipients: normalizeExternalShareEmails(
      extras?.externalRecipients ?? [],
    ),
    message: (extras?.message ?? "").trim(),
    regulationDetailUrl,
    assessmentLink: target.assessmentLink.trim(),
    attachmentS3Uri: target.attachmentS3Uri.trim(),
    sendEmailNotification: extras?.sendEmailNotification ?? true,
  };
}
